import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Gift, Percent, Tag, Sparkles, BarChart3, Zap, Calendar } from "lucide-react";

const KIND_LABEL = {
  buy_x_get_y: "اشترِ X واحصل على Y",
  cart_percent: "% خصم على السلة",
  cart_amount: "خصم بمبلغ ثابت",
  product_percent: "% خصم على منتجات محددة",
};

const KIND_ICON = {
  buy_x_get_y: Gift,
  cart_percent: Percent,
  cart_amount: Tag,
  product_percent: Sparkles,
};

const emptyForm = {
  name: "", kind: "cart_percent", active: true,
  valid_from: "", valid_to: "",
  trigger_product_id: "", trigger_qty: 2, gift_product_id: "", gift_qty: 1,
  min_amount: 0, value: 10, product_ids: [], max_uses_per_invoice: 0, coupon_code: "",
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [usageDlg, setUsageDlg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/campaigns"), api.get("/products")]);
      setItems(c.data); setProducts(p.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDlg(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, kind: c.kind, active: c.active !== false,
      valid_from: c.valid_from || "", valid_to: c.valid_to || "",
      trigger_product_id: c.trigger_product_id || "", trigger_qty: c.trigger_qty || 2,
      gift_product_id: c.gift_product_id || "", gift_qty: c.gift_qty || 1,
      min_amount: c.min_amount || 0, value: c.value || 0,
      product_ids: c.product_ids || [], max_uses_per_invoice: c.max_uses_per_invoice || 0,
      coupon_code: c.coupon_code || "",
    });
    setDlg(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("اسم الحملة مطلوب");
    if (form.kind === "buy_x_get_y" && (!form.trigger_product_id || !form.gift_product_id)) {
      return toast.error("حدد منتج الشراء والهدية");
    }
    try {
      if (editing) await api.put(`/campaigns/${editing.id}`, form);
      else await api.post("/campaigns", form);
      toast.success(editing ? "تم التحديث" : "تم إنشاء الحملة");
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (c) => {
    if (!window.confirm(`حذف حملة "${c.name}"؟`)) return;
    try { await api.delete(`/campaigns/${c.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const showUsage = async (c) => {
    try {
      const { data } = await api.get(`/campaigns/usage/${c.id}`);
      setUsageDlg(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleActive = async (c) => {
    try { await api.put(`/campaigns/${c.id}`, { ...c, active: !c.active }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="campaigns-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Sparkles className="w-6 h-6 text-primary" /> حملات الخصومات</h1>
          <p className="text-sm text-muted-foreground mt-1">أنشئ حملات ذكية: اشترِ 2 واحصل على 1 مجاناً، خصم على السلة، قسائم، خصم على منتجات محددة</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-campaign-btn"><Plus className="w-4 h-4" /> حملة جديدة</Button>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Sparkles className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد حملات بعد</div>
          <Button variant="link" onClick={openNew} className="mt-2">أنشئ الحملة الأولى</Button>
        </div>
       ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(c => {
            const Icon = KIND_ICON[c.kind] || Tag;
            const isActive = c.active !== false;
            const desc = c.kind === "buy_x_get_y"
              ? `اشترِ ${c.trigger_qty} × ${products.find(p => p.id === c.trigger_product_id)?.name || "—"} → هدية ${c.gift_qty} × ${products.find(p => p.id === c.gift_product_id)?.name || "—"}`
              : c.kind === "cart_percent" ? `${c.value}% خصم إذا الفاتورة ≥ ${c.min_amount}`
              : c.kind === "cart_amount" ? `خصم ${c.value} إذا الفاتورة ≥ ${c.min_amount}`
              : `${c.value}% خصم على ${c.product_ids?.length || 0} منتج`;
            return (
              <div key={c.id} className={`border-2 rounded-lg p-4 bg-card transition ${isActive ? "border-primary/40 hover:border-primary" : "border-slate-200 opacity-60"}`} data-testid={`campaign-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded bg-primary/10 grid place-items-center"><Icon className="w-5 h-5 text-primary" /></div>
                    <div>
                      <div className="font-bold">{c.name}</div>
                      <div className="text-[10px] text-muted-foreground">{KIND_LABEL[c.kind]}</div>
                    </div>
                  </div>
                  <Switch checked={isActive} onCheckedChange={() => toggleActive(c)} />
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2 min-h-[2rem]">{desc}</p>
                {c.coupon_code && <div className="text-[10px] font-mono bg-amber-50 text-amber-800 px-2 py-1 rounded mb-2">قسيمة: {c.coupon_code}</div>}
                {(c.valid_from || c.valid_to) && (
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1 mb-2">
                    <Calendar className="w-3 h-3" /> {c.valid_from || "—"} → {c.valid_to || "دائم"}
                  </div>
                )}
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-[10px] text-muted-foreground font-mono">استخدمت: <b className="text-foreground">{c.usage_count || 0}</b></span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => showUsage(c)} title="التقرير"><BarChart3 className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(c)}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(c)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
       )}

      {/* Edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="campaign-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل حملة" : "حملة جديدة"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>اسم الحملة</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name" /></div>
            <div>
              <Label>نوع الحملة</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="campaign-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cart_percent">% خصم على السلة</SelectItem>
                  <SelectItem value="cart_amount">خصم بمبلغ ثابت على السلة</SelectItem>
                  <SelectItem value="buy_x_get_y">اشترِ X واحصل على Y مجاناً</SelectItem>
                  <SelectItem value="product_percent">% خصم على منتجات محددة</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.kind === "buy_x_get_y" && (
              <div className="p-3 border rounded bg-emerald-50/40 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">المنتج المُشترى</Label>
                    <select className="w-full h-10 px-2 border rounded bg-background" value={form.trigger_product_id} onChange={(e) => setForm({ ...form, trigger_product_id: e.target.value })}>
                      <option value="">— اختر —</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-xs">الكمية المُشتراة</Label><Input type="number" min={1} value={form.trigger_qty} onChange={(e) => setForm({ ...form, trigger_qty: +e.target.value || 1 })} /></div>
                  <div>
                    <Label className="text-xs">منتج الهدية</Label>
                    <select className="w-full h-10 px-2 border rounded bg-background" value={form.gift_product_id} onChange={(e) => setForm({ ...form, gift_product_id: e.target.value })}>
                      <option value="">— اختر —</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-xs">كمية الهدية</Label><Input type="number" min={1} value={form.gift_qty} onChange={(e) => setForm({ ...form, gift_qty: +e.target.value || 1 })} /></div>
                </div>
                <p className="text-xs text-emerald-700">مثال: اشترِ 2 × عصير → 1 × عصير مجاناً</p>
              </div>
            )}

            {(form.kind === "cart_percent" || form.kind === "cart_amount") && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label>{form.kind === "cart_percent" ? "نسبة الخصم %" : "مبلغ الخصم"}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: +e.target.value || 0 })} /></div>
                <div><Label>الحد الأدنى للفاتورة</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: +e.target.value || 0 })} /></div>
              </div>
            )}

            {form.kind === "product_percent" && (
              <div>
                <Label>نسبة الخصم %</Label>
                <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: +e.target.value || 0 })} className="mb-2" />
                <Label>المنتجات المستهدفة</Label>
                <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                  {products.map(p => (
                    <label key={p.id} className="flex items-center gap-2 text-sm hover:bg-muted/30 p-1 rounded cursor-pointer">
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={(e) => {
                        setForm(f => ({ ...f, product_ids: e.target.checked ? [...f.product_ids, p.id] : f.product_ids.filter(x => x !== p.id) }));
                      }} />
                      <span>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div><Label>يبدأ من</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
              <div><Label>ينتهي في</Label><Input type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></div>
            </div>
            <div><Label>رمز قسيمة (اختياري — اتركه فارغاً للتطبيق التلقائي)</Label><Input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} placeholder="EID2026" /></div>
            <div className="flex items-center justify-between p-3 rounded border bg-muted/30">
              <Label>الحملة نشطة</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-campaign-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage dialog */}
      <Dialog open={!!usageDlg} onOpenChange={(o) => !o && setUsageDlg(null)}>
        <DialogContent className="max-w-2xl">
          {usageDlg && (
            <>
              <DialogHeader><DialogTitle>تقرير الحملة: {usageDlg.campaign?.name}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="p-3 rounded bg-primary/5 border border-primary/20 text-center"><div className="text-2xl font-bold font-mono">{usageDlg.usage_count}</div><div className="text-xs text-muted-foreground">فواتير</div></div>
                <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-center"><div className="text-2xl font-bold font-mono">{(usageDlg.total_customer_savings || 0).toLocaleString("ar-EG")}</div><div className="text-xs text-muted-foreground">إجمالي التوفير</div></div>
                <div className="p-3 rounded bg-slate-50 border text-center"><div className="text-2xl font-bold font-mono">{usageDlg.campaign?.usage_count || 0}</div><div className="text-xs text-muted-foreground">إجمالي المرات</div></div>
              </div>
              {usageDlg.invoices?.length ? (
                <div className="border rounded overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0"><tr><th className="p-2 text-start">#</th><th className="p-2 text-start">العميل</th><th className="p-2 text-start">المبلغ</th><th className="p-2 text-start">التاريخ</th></tr></thead>
                    <tbody>{usageDlg.invoices.map(inv => <tr key={inv.id} className="border-t"><td className="p-2 font-mono">#{inv.number}</td><td className="p-2">{inv.party_name}</td><td className="p-2 font-mono">{(inv.total || 0).toLocaleString("ar-EG")}</td><td className="p-2 text-muted-foreground">{new Date(inv.created_at).toLocaleDateString("ar-EG")}</td></tr>)}</tbody>
                  </table>
                </div>
              ) : <p className="text-center text-muted-foreground py-6">لم تُستخدم هذه الحملة بعد</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
